import { ChangeEvent } from 'react';

export enum MemoryCheckValueType {
  Variable = 'variable',
  VariableArray = 'variable[]',
}

export type MemorySingleLink = {
  type: MemoryCheckValueType.Variable;
  variableId: string;
}

export type MemoryArrayLink = {
  type: MemoryCheckValueType.VariableArray;
  variableId: string[];
}

export type MemoryLinkValue = MemorySingleLink | MemoryArrayLink;

export type MemoryValue = string | number;

/**
 * CharacterMemory values are stored as flat character IDs.
 * Keep the characterMemory form copy explicit so it does not imply nested avatar/look fields.
 */
export const DEFAULT_CHARACTER_MEMORY_PRESENTATION_LABEL = 'Default Character Skin';
export const SELECT_CHARACTER_MEMORY_PRESENTATION_PLACEHOLDER = 'Select Character Skin';

export enum MemoryType {
  String = 'string',
  Number = 'number',
  Boolean = 'boolean',
  /**
   * Canonical type for variables that hold a character ID reference.
   * Used to drive dynamic speaker selection from the player's memory bank.
   */
  Character = 'character',
}

export const MemoryTypeLabel: Record<MemoryType, string> = {
  [MemoryType.String]: 'Text',
  [MemoryType.Number]: 'Number',
  [MemoryType.Boolean]: 'Yes/No',
  [MemoryType.Character]: 'Character',
};

export function isCharacterReferenceMemoryType(type?: MemoryType | string): boolean {
  return type === MemoryType.Character;
}

/**
 * Scope type determines how a variable's value persists across playthroughs.
 * - 'playthrough': resets when the reader reverts to a save point (default)
 * - 'game': persists across all playthroughs for the same user+game
 */
export enum MemoryScopeType {
  Playthrough = 'playthrough',
  Game = 'game',
}

export const MemoryScopeTypeLabel: Record<MemoryScopeType, string> = {
  [MemoryScopeType.Playthrough]: 'Playthrough Memory',
  [MemoryScopeType.Game]: 'Lasting Memory',
};

export enum MemoryShowIn {
  Hide = 'hide',
  Alert = 'alert',
  Panel = 'panel',
  Hud = 'hud',
  Summary = 'summary',
}

export type MemoryDTO = {
  id: number,
  type: MemoryType,
  name: string,
  defaultValue: MemoryValue,
  displayName: string,
  defaultChangeDescription: string,
  icon: number,
  showIn: Array<MemoryShowIn>,
  bookId: number,
  /** Scope type for this variable — determines persistence across playthroughs */
  scopeType: MemoryScopeType,
  /** ISO timestamp when this variable was locked (at publish). Null if unlocked. */
  lockedAt: string | null,
}

/**
 * Operators for step check conditions
 */
export enum StepCheckOperatorType {
  Equal = 'equal',
  NotEqual = 'notEqual',
  Greater = 'greater',
  AtLeast = 'atLeast',
  Less = 'less',
  AtMost = 'atMost',
  Min = 'min',
  Max = 'max',
  ConditionList = 'conditionList',
  /** Check if player has a specific achievement/reward */
  PlayerHas = 'playerHas',
}

/**
 * Match types for playerHas checks with multiple rewards
 */
export enum PlayerHasMatchType {
  /** Player must have ALL specified rewards (default) */
  All = 'all',
  /** Player must have at least ONE of the specified rewards */
  Any = 'any',
}

export const PlayerHasMatchTypeLabels: Record<PlayerHasMatchType, string> = {
  [PlayerHasMatchType.All]: 'All of:',
  [PlayerHasMatchType.Any]: 'Any of:',
};

export type CardMemorySlotData = {
  name: string,
  value: MemoryValue,
  operator: StepCheckOperatorType,
}

export type MemoryFormDTO = Omit<MemoryDTO, 'defaultValue'> & {
    value: MemoryValue,
}

export type MemoryIcon = {
  id: number,
  bookId: number,
  userId: number,
  key: string,
  label: string,
  type: string,
  url: string,
}

export type StepCheck = {
  variableId: number,
  operator: StepCheckOperatorType,
  /**
   * Check value depends on operator type:
   * - `MemoryValue` (string | number) — literal value for single checks (e.g. "5", "yes")
   * - `MemorySingleLink` — variable-to-variable comparison in single checks
   * - `MemoryArrayLink` — array of variable IDs for Min/Max multi-memory checks
   * - `number[]` — reward IDs for PlayerHas checks
   * - `undefined` — ConditionList checks (values are in individual condition rows)
   */
  value?: MemoryValue | MemoryLinkValue | number[],
  /** Match mode for PlayerHas operator: ALL (player must have every reward) or ANY (at least one) */
  match?: PlayerHasMatchType,
}

export type StepCheckSwitch = {
  value?: boolean | string, // Optional for conditionList
  gotoBranchId: number,
  operator?: string,
  intValue?: string | number, // For literal integer values (frontend format)
  varValue?: number, // For variable IDs (frontend format - condition row right operand)
  number?: string | number, // For literal integer values (backend format)
  variable?: number, // For variable IDs (backend format)
}

export type StepCheckOperator = {
  title: string,
  short: string,
  isMultiSelect: boolean,
  isConditionList?: boolean,
  isPlayerHas?: boolean, // Special flag for achievement/reward checks
}

export const CheckOperator: Record<StepCheckOperatorType, StepCheckOperator> = {
  [StepCheckOperatorType.Equal]: {
    title: 'Exactly',
    short: '=',
    isMultiSelect: false,
  },
  [StepCheckOperatorType.NotEqual]: {
    title: 'Not exactly',
    short: '<>',
    isMultiSelect: false,
  },
  [StepCheckOperatorType.Greater]: {
    title: 'More than',
    short: '>',
    isMultiSelect: false,
  },
  [StepCheckOperatorType.AtLeast]: {
    title: 'At least',
    short: '>=',
    isMultiSelect: false,
  },
  [StepCheckOperatorType.Less]: {
    title: 'Less than',
    short: '<',
    isMultiSelect: false,
  },
  [StepCheckOperatorType.AtMost]: {
    title: 'At most',
    short: '<=',
    isMultiSelect: false,
  },
  [StepCheckOperatorType.Min]: {
    title: 'Lowest',
    short: 'Min',
    isMultiSelect: true,
  },
  [StepCheckOperatorType.Max]: {
    title: 'Highest',
    short: 'Max',
    isMultiSelect: true,
  },
  [StepCheckOperatorType.ConditionList]: {
    title: 'Condition List',
    short: 'Conditions',
    isMultiSelect: false,
    isConditionList: true,
  },
  [StepCheckOperatorType.PlayerHas]: {
    title: 'Player has',
    short: 'Has',
    isMultiSelect: false,
    isPlayerHas: true,
  },
};

export type ChangeMemoryEvent = Partial<ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>>;
